import { CircularProgress, Tooltip } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import CategoryIcon from "@material-ui/icons/Category";
import ChatIcon from "@material-ui/icons/Chat";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import GavelIcon from "@material-ui/icons/Gavel";
import MoneyIcon from "@material-ui/icons/Money";
import Receipt from "@material-ui/icons/Receipt";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useHistory } from "react-router-dom";
import BadgeLink from "../../Components/BadgeLink";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  getWarehouses,
  paymentStates,
  requestErrorHandler,
  tableAlternateColors,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";
import AddManufacturingEntry from "./AddManufacturingEntry";
import ManufacturingEntryDialog from "./ManufacturingEntryDialog";

const server = EnvSettings.server;

export default function Manufacturing(props) {
  const order_id = props?.computedMatch?.params?.order_id;
  const history = useHistory();
  const classes = useStyles();
  const [showManucaturingEntry, setShowManufacturingEntry] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [searchList, setSearchList] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [searchValue, setSearchValue] = useState(() => order_id || "");
  const [filterStates, setFilterStates] = useState(() =>
    order_id
      ? paymentStates.map((m) => m.value)
      : ["invoiced", "deposit", "completed", "transit"],
  );
  const [selectedOrder, setSelectedOrder] = useState();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [warehouseLocation, setWarehouseLocation] = useState();
  const dispatch = useDispatch();
  useEffect(() => {
    getWarehouses(setWarehouseLocation, dispatch);
  }, [dispatch]);
  const editableWarehouses = useMemo(
    () =>
      (warehouseLocation || []).filter((warehouse) =>
        warehouse?.editableStock === undefined
          ? false
          : Number(warehouse.editableStock) === 1 ||
            warehouse.editableStock === true,
      ),
    [warehouseLocation],
  );

  const editBoxRef = useRef(null);

  const executeScroll = () => {
    editBoxRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const showOrder = (dev) => {
    setSelectedOrder();
    setTimeout(() => {
      setSelectedOrder(dev);
      setSelectedEntry(dev);
      setSelectedEntryId(dev?.id ?? null);
      if (editBoxRef && editBoxRef.current) {
        executeScroll();
      }
    }, 1);
  };

  const fetchManufacturingEntries = () => {
    setLoading(true);
    setSearchList([]);
    setSelectedOrder();
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    };
    fetch(server + "/get_manufacturing_entries", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error && Array.isArray(result.manufacturingEntries)) {
          setOrderList(result.manufacturingEntries);
        } else if (result.error) {
          setOrderList([]);
        }
      })
      .catch(() => {
        setOrderList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filterResults = () => {
    const list = Array.isArray(orderList) ? orderList : [];
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      setSearchList(list);
      return;
    }
    const filtered = list.filter((entry) => {
      const fields = [
        entry.uuid,
        entry.batchNumber,
        entry.batchSize?.toString(),
        entry.StockItem?.name,
        entry.StockItem?.sku,
        entry.SerialisedSubAssembly?.number,
        entry.Device?.serialNumber,
        entry.Device?.chassisNumber,
        entry.metadata?.producedDevice?.serialNumber,
        entry.metadata?.producedDevice?.chassisNumber,
        ...(entry.metadata?.consumedSerialisedSubAssemblies || []).map(
          (subAssembly) => subAssembly.number,
        ),
        entry.StockChange?.Warehouse?.name,
      ];
      return fields.some((field) => {
        if (!field && field !== 0) {
          return false;
        }
        return field.toString().toLowerCase().includes(term);
      });
    });
    setSearchList(filtered);
  };

  useEffect(filterResults, [searchValue, orderList, filterStates]);

  useEffect(fetchManufacturingEntries, []);

  useEffect(() => {
    if (!selectedEntryId) {
      return;
    }
    const updatedEntry = (orderList || []).find(
      (entry) => entry.id === selectedEntryId,
    );
    if (updatedEntry) {
      setSelectedEntry(updatedEntry);
    }
  }, [orderList, selectedEntryId]);

  return (
    <Grid container spacing={3}>
      <AddManufacturingEntry
        showTicketEntry={showManucaturingEntry}
        setShowTicketEntry={setShowManufacturingEntry}
        warehouses={editableWarehouses}
        onEntryCreated={fetchManufacturingEntries}
      />
      <ManufacturingEntryDialog
        open={!!selectedEntry}
        entry={selectedEntry}
        onClose={() => {
          setSelectedEntry(null);
          setSelectedEntryId(null);
        }}
        onRefresh={fetchManufacturingEntries}
      />
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <BadgeLink
              title={t("manufacturing.quickProduction", "Quick production")}
              icon={GavelIcon}
              onClick={() => {
                setShowManufacturingEntry(true);
              }}
            />
            <BadgeLink
              title={t("manufacturing.recipes", "Recipes")}
              icon={FormatListBulletedIcon}
              href="/cpanel/manufacturing/recipes"
            />
            <BadgeLink
              title={t("manufacturing.orders", "Manufacturing orders")}
              icon={Receipt}
              href={`${window.location.pathname.startsWith("/cpanel") ? "/cpanel" : ""}/manufacturing/orders`}
            />
            <BadgeLink
              title={t("manufacturing.featureLists", "Feature lists")}
              icon={CategoryIcon}
              href="/cpanel/manufacturing/features"
            />
            <BadgeLink
              title={t(
                "manufacturing.serialisedSubAssemblies",
                "Serialised sub-assemblies",
              )}
              icon={MoneyIcon}
              href="/cpanel/manufacturing/serialisedsubassemblies"
            />
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper} style={{ overflow: "hidden" }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item container spacing={3} xs={12}>
              <Grid item xs={12}>
                <Title>{t("manufacturing.header")}</Title>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                autoFocus
                label={t("search")}
                type="text"
                fullWidth
                variant="outlined"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                ref={editBoxRef}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <SortableTable
                tableLayout={"fixed"}
                downloadButton={true}
                header={t("manufacturing.entries")}
                headCells={[
                  {
                    id: "Notes",
                    label: "",
                    width: 32,
                    hideFromCsv: true,
                  },
                  // {
                  //   id: "EmailListResupplyOrders",
                  //   label: "",
                  //   width: 32,
                  //   hideFromCsv: true,
                  // },
                  {
                    id: "createdAt",
                    numeric: false,
                    disablePadding: false,
                    label: t("createdAt"),
                  },
                  {
                    id: "uuid",
                    numeric: false,
                    disablePadding: false,
                    label: "Unique ID",
                  },
                  {
                    id: "batchNumber",
                    numeric: false,
                    disablePadding: false,
                    label: t("manufacturing.batchNumber"),
                  },
                  {
                    id: "batchSize",
                    numeric: false,
                    disablePadding: false,
                    label: t("manufacturing.batchSize"),
                  },
                  {
                    id: "StockItem.sku",
                    numeric: false,
                    disablePadding: false,
                    label: t("stockModule.sku"),
                  },
                  {
                    // Warehouse data has to be accessed from StockChanges array
                    id: "StockChanges",
                    numeric: false,
                    disablePadding: false,
                    label: t("manufacturing.manufacturedIn"),
                  },
                  {
                    id: "SerialisedSubAssembly.number",
                    numeric: false,
                    disablePadding: false,
                    label: t("manufacturing.serialisedSubAssemblyNumber"),
                  },
                  {
                    id: "Device.serialNumber",
                    numeric: false,
                    disablePadding: false,
                    label: t("manufacturing.completedDevice", "Device"),
                  },
                  {
                    id: "totalCost",
                    numeric: false,
                    disablePadding: false,
                    label: t(
                      "manufacturing.manufacturingCost",
                      "Manufacturing Cost",
                    ),
                  },
                ]}
                // disablePagination: true,
                defaultSortRow={"createdAt"}
                handleRowClick={showOrder}
                alternateColors={true}
                getRowStyle={tableAlternateColors}
                specialDataFormatting={[
                  {
                    id: "updatedAt",
                    format: (data, d, dd) => {
                      if (dd.Notes && dd.Notes.length) {
                        const max = dd.Notes.reduce((prev, current) =>
                          prev && prev.id > current.id ? prev : current,
                        );
                        return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                      }
                      return formatTimeWithTZOffset(data, ECABIN);
                    },
                  },
                  {
                    id: "Notes",
                    format: (data) => {
                      if (data.find((f) => f.User)) {
                        return (
                          <Tooltip title={t("activeComments")}>
                            <ChatIcon
                              color="primary"
                              style={{ fontSize: 20 }}
                            />
                          </Tooltip>
                        );
                      }
                      return "";
                    },
                  },
                  {
                    id: "StockChanges",
                    format: (data) => {
                      return data?.[0]?.Warehouse?.label || "";
                    },
                  },
                  {
                    id: "createdAt",
                    format: (data) => {
                      return formatTimeWithTZOffset(data, ECABIN);
                    },
                  },
                ]}
                // handleScrollClick: showDeviceNewTab,
                tableData={searchList}
                rowsPerPage={50}
              />
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
